import { useEffect, useState } from 'react';
import { StoreSuggestionsSchema, type StoreSuggestionsDto } from '@xshop/shared';
import { fetchApi, getStoreProductsPage, type StorefrontProduct } from '../api/client';
import { useStoreRead, type StoreRead } from '../elements/render/useStoreRead';

// What the search panel reads: the suggestions for the words typed, once the shopper pauses, and the
// shop's newest products for the moment before anything is typed. Each is one bounded read through the
// hook every store-bound element uses, so the panel waits, fails and recovers the same way they do.

/** How long typing must pause before the words are sent, so a word is asked for once, not per letter. */
const PAUSE_MS = 200;

/** How many products the panel offers before anything is typed. */
const POPULAR = 4;

// Holds a value back until it has stopped changing for a moment.
export function usePaused<T>(value: T, ms = PAUSE_MS): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

// Asks the store what matches the words, and refuses an answer that is not the shape the panel draws.
async function readSuggestions(words: string, signal: AbortSignal): Promise<StoreSuggestionsDto> {
  const raw = await fetchApi<unknown>(`/products/suggest?q=${encodeURIComponent(words)}`, { signal });
  return StoreSuggestionsSchema.parse(raw);
}

// Reads the suggestions for the words typed, or nothing while there are none.
export const useSearchSuggestions = (words: string): StoreRead<StoreSuggestionsDto> =>
  useStoreRead(words ? `suggest:${words}` : null, (signal) => readSuggestions(words, signal));

// Reads the shop's newest products, which the panel offers before anything is typed.
export const usePopularProducts = (enabled: boolean): StoreRead<StorefrontProduct[]> =>
  useStoreRead(enabled ? `popular:${POPULAR}` : null, async () => (await getStoreProductsPage(POPULAR)).items);
